import { Helmet } from 'react-helmet-async';
import { useEffect, useRef, useCallback, useState } from 'react';
import { AidaFAQ } from '../components/aida/AidaFAQ';
import { isMobileDevice, isNonWindowsOS } from '../../utility/utility';
import { SignedIn, SignedOut, SignInButton, useUser } from '@clerk/clerk-react';
import { download, setAutoDownloadFlag, sendDownloadReminderEmail } from '../../utility/api';
import { toast } from 'sonner';
import '../../styles/aida-base.css';
import '../../styles/aida-responsive.css';

export const HostOutbreak = () => {
  const { user } = useUser();

  const [breakpoint, setBreakpoint] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [contentScale, setContentScale] = useState(1);
  const [heroScale, setHeroScale] = useState(1);
  const [isContentReady, setIsContentReady] = useState(false);

  const showJoinButton = isNonWindowsOS() || isMobileDevice();

  // Handler for joining network (when not signed in)
  const handleJoinNetworkClick = useCallback(() => {
    setAutoDownloadFlag(isMobileDevice() || isNonWindowsOS());
  }, []);

  // Handler for download button (when signed in)
  const handleDownloadClick = useCallback(async () => {
    // Check if user is on mobile device or non-Windows OS
    if (isMobileDevice() || isNonWindowsOS()) {
      // On mobile or non-Windows OS, send email reminder instead of downloading
      const email = user?.primaryEmailAddress?.emailAddress;
      if (email) {
        await sendDownloadReminderEmail(email);
        toast.success('Download reminder email sent!', {
          description: 'Check your email for a link to download Playcast on your PC.',
          duration: 5000,
          style: {
            background: '#fbbf24',
            color: '#000000',
            border: 'none',
          },
        });
      } else {
        toast.error('Unable to send reminder', {
          description: 'Email address not found.',
          style: {
            background: '#fbbf24',
            color: '#000000',
            border: 'none',
          },
        });
      }
    } else {
      // On Windows desktop, proceed with normal download
      download();
    }
  }, [user]);


  // Clean scroll system: wait for content, then scroll to hash or top
  useEffect(() => {
    // Prevent browser's default scroll restoration
    window.history.scrollRestoration = 'manual';
  }, []);

  // Once content is ready, perform the final scroll
  useEffect(() => {
    if (isContentReady) {
      const hash = window.location.hash;

      if (hash) {
        // Hash present: scroll to that section
        setTimeout(() => {
          const element = document.getElementById(hash.substring(1));
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100); // Small delay to ensure rendering is complete
      } else {
        // No hash: stay at top
        window.scrollTo(0, 0);
      }
    }
  }, [isContentReady]);

  // Responsive breakpoint detection
  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width <= 768) setBreakpoint('mobile');
      else if (width <= 1024) setBreakpoint('tablet');
      else setBreakpoint('desktop');
    };

    updateBreakpoint();
    window.addEventListener('resize', updateBreakpoint);
    return () => window.removeEventListener('resize', updateBreakpoint);
  }, []);

  // Hero section scaling - scales entire section proportionally
  useEffect(() => {
    const updateHeroScale = () => {
      const viewportWidth = window.innerWidth;
      const designWidth = 1024; // Design width (hero section rendered at 1024px)

      // Different padding for mobile vs desktop
      let viewportPercentage;
      if (viewportWidth <= 768) {
        // Mobile: use 100% of viewport (no padding)
        viewportPercentage = 1.0;
      } else if (viewportWidth <= 1024) {
        // Tablet: use 94% of viewport
        viewportPercentage = 0.94;
      } else {
        // Desktop: use 92% of viewport (4% padding each side)
        viewportPercentage = 0.92;
      }

      const availableWidth = viewportWidth * viewportPercentage;
      const scale = availableWidth / designWidth;
      setHeroScale(scale);
    };

    updateHeroScale();
    window.addEventListener('resize', updateHeroScale);
    return () => window.removeEventListener('resize', updateHeroScale);
  }, []);

  // Update content scale for tablet only (between mobile and desktop)
  useEffect(() => {
    const updateContentScale = () => {
      const width = window.innerWidth;
      // Only scale on tablet (769px - 1023px), not on mobile
      if (width > 768 && width < 1024) {
        setContentScale(width / 1024);
      } else {
        setContentScale(1);
      }
    };

    updateContentScale();
    window.addEventListener('resize', updateContentScale);
    return () => window.removeEventListener('resize', updateContentScale);
  }, []);

  // Dynamic FAQ scaling based on viewport width
  useEffect(() => {
    const updateFaqScale = () => {
      const viewportWidth = window.innerWidth;
      const designWidth = 1024;
      const designHeight = 540; // Fixed height from AidaFAQ component
      const mobileBreakpoint = 1024;

      const faqWrapper = document.getElementById('faq-section');

      if (faqWrapper) {
        if (viewportWidth <= mobileBreakpoint) {
          // Mobile: no scaling
          faqWrapper.style.transform = 'none';
          faqWrapper.style.height = 'auto';
        } else {
          // Desktop: apply proportional scaling
          const scale = viewportWidth / designWidth;
          const scaledHeight = designHeight * scale;

          // Apply scale and set height to accommodate scaled content
          faqWrapper.style.transform = `scale(${scale})`;
          faqWrapper.style.transformOrigin = 'top center';
          faqWrapper.style.height = `${scaledHeight}px`;
        }
      }
    };

    // Wait for content to load before scaling
    let contentReadySet = false;

    const handleLoad = () => {
      updateFaqScale();
      if (!contentReadySet) {
        contentReadySet = true;
        setIsContentReady(true);
      }
    };

    // Check if already loaded
    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }

    // Fallback: Show content after 2 seconds even if load event doesn't fire
    const fallbackTimeout = setTimeout(() => {
      if (!contentReadySet) {
        updateFaqScale();
        contentReadySet = true;
        setIsContentReady(true);
      }
    }, 2000);

    // Real-time resize handler
    window.addEventListener('resize', updateFaqScale);

    return () => {
      window.removeEventListener('load', handleLoad);
      window.removeEventListener('resize', updateFaqScale);
      clearTimeout(fallbackTimeout);
    };
  }, []);

  // Responsive styles
  const styles = {
    pageWrapper: {
      backgroundColor: '#282828',
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100vw',
      margin: 0,
      padding: 0,
      overflowX: 'hidden' as const,
      fontFamily: 'Inter, -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Ubuntu, Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
    },
    heroSection: {
      position: 'relative' as const,
      width: '100%',
      minHeight: 'auto',
      background: 'linear-gradient(180deg, #0b0b1c 0%, #18173e 10%, #18173e 70%, black 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: breakpoint === 'mobile' ? '80px 20px 60px' : 'clamp(200px, 14vw, 240px) clamp(20px, 2vw, 40px)',
      overflow: 'visible',
    },
    heroContent: {
      position: 'relative' as const,
      width: '100%',
      maxWidth: breakpoint === 'mobile' ? '768px' : '1920px',
      display: 'flex',
      flexDirection: (breakpoint === 'mobile' ? 'column' : 'row') as const,
      alignItems: 'center',
      justifyContent: breakpoint === 'mobile' ? 'center' : 'space-between',
      gap: breakpoint === 'mobile' ? '20px' : 'clamp(20px, 3vw, 60px)',
      color: '#ffffff',
      padding: breakpoint === 'mobile' ? '0' : '0 clamp(20px, 4vw, 80px)',
      margin: breakpoint === 'mobile' ? '0 auto' : '0',
      boxSizing: 'border-box' as const,
    },
    heroTextContent: {
      flex: breakpoint === 'mobile' ? 'none' : '1 1 60%',
      width: breakpoint === 'mobile' ? '100%' : 'auto',
      textAlign: (breakpoint === 'mobile' ? 'center' : 'left') as const,
      order: breakpoint === 'mobile' ? 2 : 'initial',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: breakpoint === 'mobile' ? 'center' : 'flex-start',
    },
    heroVisuals: {
      position: 'relative' as const,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: breakpoint === 'mobile' ? 'none' : '0 1 40%',
      width: breakpoint === 'mobile' ? '90%' : 'auto',
      maxWidth: breakpoint === 'mobile' ? '400px' : '500px',
      order: breakpoint === 'mobile' ? 1 : 'initial',
    },
    heroImage: {
      width: '100%',
      height: 'auto',
    },
    heroHeading: {
      fontSize: breakpoint === 'mobile' ? 'clamp(36px, 9vw, 48px)' : 'clamp(64px, 6vw, 96px)',
      fontWeight: 900,
      fontFamily: 'Inter, sans-serif',
      marginBottom: breakpoint === 'mobile' ? '20px' : 'clamp(15px, 2vw, 30px)',
      lineHeight: '1.2',
      color: '#ffffff',
      margin: breakpoint === 'mobile' ? '0 0 20px 0' : '0 0 clamp(15px, 2vw, 30px) 0',
      whiteSpace: 'pre-line' as const,
      letterSpacing: '-0.5px',
    },
    heroSubheading: {
      fontSize: breakpoint === 'mobile' ? 'clamp(16px, 4.5vw, 20px)' : 'clamp(20px, 2vw, 32px)',
      fontWeight: 400,
      fontFamily: 'Inter, sans-serif',
      marginBottom: breakpoint === 'mobile' ? '40px' : 'clamp(30px, 3vw, 50px)',
      lineHeight: '1.6',
      color: '#ffffff',
      opacity: 0.9,
    },
    ctaButton: {
      backgroundColor: '#FBBF24',
      color: '#000000',
      fontSize: breakpoint === 'mobile' ? 'clamp(14px, 3.5vw, 16px)' : 'clamp(16px, 1.2vw, 20px)',
      fontWeight: 600,
      fontFamily: 'Inter, sans-serif',
      padding: breakpoint === 'mobile' ? '14px 32px' : 'clamp(14px, 1.2vw, 20px) clamp(32px, 3vw, 50px)',
      borderRadius: '40px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      width: breakpoint === 'mobile' ? '100%' : 'auto',
      maxWidth: breakpoint === 'mobile' ? '300px' : 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      justifyContent: 'center',
      textDecoration: 'none',
      textAlign: 'center' as const,
      letterSpacing: '-0.08px',
    },
    ctaWrapper: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      width: breakpoint === 'mobile' ? '100%' : 'auto',
    },
    termsLink: {
      display: 'block',
      marginTop: '12px',
      fontSize: '12px',
      color: '#ffffff',
      opacity: 0.7,
      textAlign: 'center' as const,
      textDecoration: 'underline',
      fontFamily: 'Inter, sans-serif',
    },
    rewardsSection: {
      backgroundColor: '#000000',
      padding: breakpoint === 'mobile' ? '30px 20px' : 'clamp(30px, 4vw, 60px) clamp(20px, 2vw, 40px)',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
    },
    rewardsHeading: {
      fontSize: breakpoint === 'mobile' ? 'clamp(28px, 8vw, 36px)' : 'clamp(42px, 5vw, 72px)',
      fontWeight: 900,
      fontFamily: 'Inter, sans-serif',
      color: '#ffffff',
      textAlign: 'center' as const,
      marginBottom: breakpoint === 'mobile' ? '25px' : 'clamp(25px, 3vw, 40px)',
      letterSpacing: '1px',
    },
    cardsContainer: {
      display: breakpoint === 'mobile' ? 'flex' : 'grid',
      flexDirection: breakpoint === 'mobile' ? ('column' as const) : undefined,
      gridTemplateColumns: breakpoint === 'mobile' ? undefined : 'repeat(3, 1fr)',
      gap: breakpoint === 'mobile' ? '20px' : 'clamp(15px, 2vw, 30px)',
      width: '100%',
      maxWidth: breakpoint === 'mobile' ? '768px' : '1920px',
      marginBottom: breakpoint === 'mobile' ? '25px' : 'clamp(25px, 3vw, 40px)',
      padding: breakpoint === 'mobile' ? '0' : '0 clamp(20px, 4vw, 80px)',
      boxSizing: 'border-box' as const,
    },
    card: {
      backgroundColor: '#3d4466',
      borderRadius: breakpoint === 'mobile' ? '20px' : 'clamp(15px, 1.5vw, 25px)',
      padding: breakpoint === 'mobile' ? '35px 25px 15px' : 'clamp(30px, 3vw, 50px) clamp(25px, 2.5vw, 40px) clamp(15px, 1.5vw, 25px)',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'flex-start',
      textAlign: 'left' as const,
      overflow: 'visible',
    },
    cardHeading: {
      fontSize: breakpoint === 'mobile' ? 'clamp(18px, 6vw, 30px)' : 'clamp(24px, 2.2vw, 36px)',
      fontWeight: 800,
      fontFamily: 'Inter, sans-serif',
      color: '#ffffff',
      marginBottom: breakpoint === 'mobile' ? '15px' : 'clamp(12px, 1.2vw, 20px)',
      lineHeight: '1.3',
    },
    cardText: {
      fontSize: breakpoint === 'mobile' ? 'clamp(13px, 4vw, 18px)' : 'clamp(16px, 1.3vw, 22px)',
      fontWeight: 400,
      fontFamily: 'Inter, sans-serif',
      color: '#c8c8d8',
      lineHeight: '1.6',
      marginBottom: breakpoint === 'mobile' ? '30px' : 'clamp(25px, 2.5vw, 40px)',
    },
    cardIcon: {
      width: '200%',
      maxWidth: 'none',
      height: 'auto',
      marginTop: '20px',
      alignSelf: 'center',
    },
    disclaimer: {
      fontSize: breakpoint === 'mobile' ? '12px' : 'clamp(12px, 1vw, 16px)',
      fontStyle: 'italic',
      color: '#888888',
      textAlign: 'center' as const,
      width: '100%',
      maxWidth: breakpoint === 'mobile' ? '768px' : '1920px',
      marginBottom: breakpoint === 'mobile' ? '25px' : 'clamp(25px, 3vw, 40px)',
      padding: breakpoint === 'mobile' ? '0' : '0 clamp(20px, 4vw, 80px)',
      fontFamily: 'Inter, sans-serif',
      boxSizing: 'border-box' as const,
    },
  };

  const downloadIcon = (
    <svg
      style={{ width: '16px', height: '16px' }}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 512 512"
      fill="currentColor"
    >
      <path d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 242.7-73.4-73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0l128-128c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 274.7 288 32zM64 352c-35.3 0-64 28.7-64 64l0 32c0 35.3 28.7 64 64 64l384 0c35.3 0 64-28.7 64-64l0-32c0-35.3-28.7-64-64-64l-101.5 0-45.3 45.3c-25 25-65.5 25-90.5 0L165.5 352 64 352zm368 56a24 24 0 1 1 0 48 24 24 0 1 1 0-48z"/>
    </svg>
  );

  return (
    <>
      <Helmet>
        <title>Host Outbreak - Earn Rewards | Playcast</title>
        <meta
          name='description'
          content='Host Outbreak on Playcast and earn rewards while providing amazing gaming experiences to players worldwide'
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap" rel="stylesheet" />
        <link href="https://fonts.googleapis.com/css?family=Roboto:400,700&display=swap" rel="stylesheet" />
      </Helmet>

      <div style={styles.pageWrapper}>
        {/* Hero Section */}
        <section style={styles.heroSection}>
          <div style={styles.heroContent}>
            {/* Text Content */}
            <div style={styles.heroTextContent}>
              <h1 style={styles.heroHeading}>
                JOIN PLAYCAST,<br />
                GET OUTBREAK<br />
                FOR <span style={{ color: '#FBBF24' }}>FREE</span>
              </h1>
              <p style={styles.heroSubheading}>
                Become part of the Playcast Network and join the gaming revolution.
              </p>
              <div style={styles.ctaWrapper}>
                <SignedIn>
                  <button
                    onClick={handleDownloadClick}
                    style={styles.ctaButton}
                    onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                      if (breakpoint !== 'mobile') {
                        e.currentTarget.style.backgroundColor = '#F59E0B';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                      if (breakpoint !== 'mobile') {
                        e.currentTarget.style.backgroundColor = '#FBBF24';
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    {!showJoinButton && downloadIcon}
                    {showJoinButton ? 'Join our Network' : 'Download Playcast to Get Rewards'}
                  </button>
                </SignedIn>
                <SignedOut>
                  <SignInButton mode="modal">
                    <button
                      onClick={handleJoinNetworkClick}
                      style={styles.ctaButton}
                      onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                        if (breakpoint !== 'mobile') {
                          e.currentTarget.style.backgroundColor = '#F59E0B';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }
                      }}
                      onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                        if (breakpoint !== 'mobile') {
                          e.currentTarget.style.backgroundColor = '#FBBF24';
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                    >
                      {!showJoinButton && downloadIcon}
                      {showJoinButton ? 'Join our Network' : 'Download Playcast to Get Rewards'}
                    </button>
                  </SignInButton>
                </SignedOut>
                <a href="/terms-and-conditions" style={styles.termsLink}>
                  Terms & Conditions
                </a>
              </div>
            </div>

            {/* Visual Assets */}
            <div style={styles.heroVisuals}>
              <img
                src="/outbreak/images/outbreak-steam-card.png"
                alt="Outbreak - Get it Free"
                style={styles.heroImage}
              />
            </div>
          </div>
        </section>

        {/* Rewards Section */}
        <section style={styles.rewardsSection}>
          <h2 style={styles.rewardsHeading}>GET REWARDS INSTANTLY</h2>

          <div style={styles.cardsContainer}>
            {/* Card 1 */}
            <div style={styles.card}>
              <h3 style={styles.cardHeading}>INSTALL THE PLAYCAST APP</h3>
              <p style={styles.cardText}>
                Install Playcast on your PC.
              </p>
              <div style={{ flex: 1 }} />
              <img
                src="/outbreak/images/card-icon-download.png"
                alt="Install Playcast"
                style={styles.cardIcon}
              />
            </div>

            {/* Card 2 */}
            <div style={styles.card}>
              <h3 style={styles.cardHeading}>AUTHENTICATE WITH PLAYCAST</h3>
              <p style={styles.cardText}>
                Create your account and get access to our rewards system.
              </p>
              <div style={{ flex: 1 }} />
              <img
                src="/outbreak/images/card-icon-auth.png"
                alt="Authenticate with Playcast"
                style={{ ...styles.cardIcon, width: '150%', position: 'relative' as const, top: '-45px' }}
              />
            </div>

            {/* Card 3 */}
            <div style={styles.card}>
              <h3 style={styles.cardHeading}>GET YOUR FREE STEAM CODE</h3>
              <p style={styles.cardText}>
                Playcast will email you your free Steam code!
              </p>
              <div style={{ flex: 1 }} />
              <img
                src="/outbreak/images/card-icon-email.png"
                alt="Get Free Steam Code"
                style={{ ...styles.cardIcon, width: '150%', position: 'relative' as const, top: '-45px' }}
              />
            </div>
          </div>

          <p style={styles.disclaimer}>
            *Offer valid for new Playcast hosts only. Limited codes available. Steam codes will be distributed on a first come, first serve basis.
          </p>

          <SignedIn>
            <button
              onClick={handleDownloadClick}
              style={styles.ctaButton}
              onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                if (breakpoint !== 'mobile') {
                  e.currentTarget.style.backgroundColor = '#F59E0B';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                if (breakpoint !== 'mobile') {
                  e.currentTarget.style.backgroundColor = '#FBBF24';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              {!showJoinButton && downloadIcon}
              {showJoinButton ? 'Join our Network' : 'Download Playcast to Get Rewards'}
            </button>
          </SignedIn>
          <SignedOut>
            <SignInButton mode="modal">
              <button
                onClick={handleJoinNetworkClick}
                style={styles.ctaButton}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                  if (breakpoint !== 'mobile') {
                    e.currentTarget.style.backgroundColor = '#F59E0B';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                  if (breakpoint !== 'mobile') {
                    e.currentTarget.style.backgroundColor = '#FBBF24';
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
              >
                {!showJoinButton && downloadIcon}
                {showJoinButton ? 'Join our Network' : 'Download Playcast for Windows'}
              </button>
            </SignInButton>
          </SignedOut>
        </section>

        {/* FAQ Section */}
        <div style={{
          width: '100%',
          backgroundColor: '#000000',
          display: 'flex',
          justifyContent: 'center',
          paddingTop: breakpoint === 'mobile' ? '40px' : '60px',
          paddingBottom: breakpoint === 'mobile' ? '60px' : '80px',
          overflow: 'hidden',
        }}>
          <div
            id="faq-section"
            style={{
              width: '100%',
              maxWidth: '1024px',
            }}
          >
            <AidaFAQ />
          </div>
        </div>
      </div>
    </>
  );
};
